import React from 'react';
import { RadioButtonProps } from './RadioButton';

export interface RadioGroupItem {
  id: string;
  disabled?: boolean;
  value: number;
}

export interface RadioGroupProps {
  items: Array<RadioGroupItem>;
  onChange: (index: number) => void;
  children: React.ReactNode;
  ['aria-labelledby']: string;
  ['data-e2e']: string;
  className?: string;
}

const RadioGroup = (props: RadioGroupProps) => {
  const {
    onChange,
    children,
    items,
    'aria-labelledby': ariaLabelledBy,
    className,
    'data-e2e': dataE2E,
  } = props;

  const setCheckedToPreviousItem = (index: number) => {
    const filteredItems = items.filter(i => !i.disabled);
    const filteredIndex = filteredItems.findIndex(
      f => f.id === items.find((_, i) => i === index)?.id
    );

    const firstIndex = 0;
    const lastIndex = Math.max(filteredItems.length - 1, 0);
    let previousIndex = lastIndex;

    if (filteredIndex !== firstIndex) {
      previousIndex = filteredIndex - 1;
    }

    const previousOption = filteredItems.find(
      (_, optionIndex) => previousIndex === optionIndex
    );

    if (!previousOption) return;
    onChange(previousOption.value);

    const previous = document.getElementById(previousOption?.id || '');
    previous?.focus();
  };

  const setCheckedToNextItem = (index: number) => {
    const filteredItems = items.filter(i => !i.disabled);
    const filteredIndex = filteredItems.findIndex(
      f => f.id === items.find((_, i) => i === index)?.id
    );

    const firstIndex = 0;
    const lastIndex = Math.max(filteredItems.length - 1, 0);
    let nextIndex = firstIndex;

    if (firstIndex === lastIndex) return;
    if (filteredIndex !== lastIndex) {
      nextIndex = filteredIndex + 1;
    }

    const nextOption = filteredItems.find(
      (_, optionIndex) => nextIndex === optionIndex
    );

    if (!nextOption) return;
    onChange(nextOption.value);

    const next = document.getElementById(nextOption?.id || '');
    next?.focus();
  };

  /**
   *
   * Handle arrow up/left/down/right to circle through list of items
   */
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const inputIndex = parseInt(e.currentTarget.value, 10);
    let flag = false;
    switch (e.key) {
      case 'Up':
      case 'ArrowUp':
      case 'Left':
      case 'ArrowLeft':
        flag = true;
        setCheckedToPreviousItem(inputIndex);
        break;

      case 'Down':
      case 'ArrowDown':
      case 'Right':
      case 'ArrowRight':
        flag = true;
        setCheckedToNextItem(inputIndex);
        break;

      default:
        break;
    }
    if (flag) {
      e.stopPropagation();
      e.preventDefault();
    }
  };

  const renderChildren = () => {
    return React.Children.map(children, child => {
      if (!React.isValidElement(child)) return child;
      const element = child as React.ReactElement<RadioButtonProps>;
      // if not numeric value, return this child as is for no-op
      // EG: this is used for the LegacyStudioRetirementNotice component
      // todo: once this is phased out we can remove this intermediate logic.
      if (typeof element.props?.value !== 'number') {
        return child;
      }
      return React.cloneElement(element, {
        onKeyDown: !element.props.disabled ? handleKeyPress : null,
      } as RadioButtonProps);
    });
  };

  return (
    <div
      className={className}
      role="radiogroup"
      aria-labelledby={ariaLabelledBy}
      data-e2e={dataE2E}
    >
      {renderChildren()}
    </div>
  );
};

export default RadioGroup;
